// admin/src/pages/MarketplaceManagementPage.jsx - 중고장터 관리
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Table, Input, Select, Modal, Form, Switch, Tooltip, Drawer, Empty, InputNumber, message } from 'antd';
import {
  ShoppingBag,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  Store,
  Search,
  X,
  Eye,
  ImagePlus,
  Archive,
  BadgeDollarSign,
  RotateCcw,
  EyeOff,
  Sparkles
} from 'lucide-react';
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { db, storage, appId } from '../firebase/firebaseConfig.jsx';
import { useUser } from '../contexts/UserContext';
import apiClient from '../services/apiClient';
import logger from '../utils/logger.jsx';

const FEE_RATE = 0.03;
const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const ITEMS_PATH = `artifacts/${appId}/marketplace`;

const CATEGORIES = ['electronics', 'clothing', 'home', 'food', 'furniture', 'other'];
const CONDITIONS = ['new', 'like_new', 'good', 'fair', 'poor'];
const STATUSES = ['available', 'reserved', 'sold', 'inactive'];

const STATUS_CLS = {
  available: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  reserved: 'bg-amber-50 text-amber-700 ring-amber-200',
  sold: 'bg-slate-100 text-slate-600 ring-slate-200',
  completed: 'bg-slate-100 text-slate-600 ring-slate-200',
  inactive: 'bg-red-50 text-red-700 ring-red-200'
};

const isSold = (s) => s === 'sold' || s === 'completed';
const isLive = (s) => s === 'available' || s === 'reserved';

const parsePrice = (price) => Number(String(price ?? '').replace(/[^\d.]/g, '')) || 0;
const calcFee = (price) => {
  const amount = parsePrice(price);
  const feeKRW = Math.floor(amount * FEE_RATE);
  return { amount, feeKRW, netAmountKRW: Math.max(0, amount - feeKRW) };
};
const won = (n) => `₩${Number(n || 0).toLocaleString()}`;
const fmtDate = (d, withTime = false) => (d ? dayjs(d).format(withTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD') : '—');

const normalizeType = (raw) => {
  if (raw === 'buy' || raw === 'buying') return 'buying';
  if (raw === 'sell' || raw === 'selling' || !raw) return 'selling';
  return raw;
};

const mapItem = (d) => {
  const data = d.data() || {};
  const photos = Array.isArray(data.photos) ? data.photos.filter(Boolean) : [];
  return {
    id: d.id,
    title: data.title || '',
    description: data.description || '',
    price: data.price ?? '',
    location: data.location || '',
    category: data.category || 'other',
    condition: data.condition || 'good',
    type: normalizeType(data.type),
    status: data.status || 'available',
    userId: data.userId || '',
    userName: data.userName || '',
    userEmail: data.userEmail || '',
    photos,
    imageUrl: data.imageUrl || photos[0] || '',
    postedByAdmin: data.postedByAdmin === true || data.isOfficial === true,
    feeKRW: data.feeKRW ?? null,
    feeWaived: data.feeWaived === true,
    paymentStatus: data.paymentStatus || null,
    likes: data.likes || 0,
    completedAt: data.completedAt?.toDate?.() || null,
    createdAt: data.createdAt?.toDate?.() || null,
    updatedAt: data.updatedAt?.toDate?.() || null
  };
};

const btn = 'inline-flex !h-8 !min-h-0 items-center gap-1.5 rounded-md !px-3 !py-0 text-xs font-semibold disabled:opacity-50';

const Thumb = ({ url, size = 'h-10 w-10' }) =>
  url ? (
    <img src={url} alt="" className={`${size} flex-shrink-0 rounded-md bg-slate-100 object-cover`} />
  ) : (
    <div className={`${size} flex flex-shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400`}>
      <ShoppingBag className="h-4 w-4" />
    </div>
  );

const MarketplaceManagementPage = () => {
  const { t: tr } = useTranslation();
  const T = (key, opts) => tr(`marketplaceAdmin.${key}`, opts);
  const { user, profile } = useUser();

  const [activeTab, setActiveTab] = useState('items');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [archive, setArchive] = useState([]);
  const [feeLedger, setFeeLedger] = useState([]);
  const [historyError, setHistoryError] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [purging, setPurging] = useState(false);
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const priceValue = Form.useWatch('price', form);
  const feeWaivedValue = Form.useWatch('feeWaived', form);

  const sellerName = profile?.displayName || profile?.name || 'Kairos';
  const sellerEmail = user?.email || profile?.email || 'admin@kairos.com';

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadItems = useCallback(async () => {
    const snap = await getDocs(query(collection(db, ITEMS_PATH), orderBy('createdAt', 'desc'), limit(500)));
    setItems(snap.docs.map(mapItem));
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryError(null);
    const [archiveRes, feeRes] = await Promise.allSettled([
      apiClient.get('/api/marketplace/admin/archive', { limit: 300 }),
      apiClient.get('/api/marketplace/fees/summary', { limit: 300 })
    ]);
    if (archiveRes.status === 'fulfilled') setArchive(archiveRes.value.data?.entries || []);
    if (feeRes.status === 'fulfilled') setFeeLedger(feeRes.value.data?.entries || []);
    const failed = [archiveRes, feeRes].find((r) => r.status === 'rejected');
    if (failed) setHistoryError(failed.reason?.message || 'error');
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadItems(), loadHistory()]);
    } catch (error) {
      logger.error('MarketplaceManagement load failed', { error: error.message });
      message.error(T('msg.loadFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadItems, loadHistory]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ---------- 통계 / 필터 ----------
  const stats = useMemo(() => {
    const s = { total: items.length, available: 0, reserved: 0, official: 0, staleSold: 0 };
    items.forEach((i) => {
      if (i.status === 'available') s.available++;
      if (i.status === 'reserved') s.reserved++;
      if (i.postedByAdmin) s.official++;
      if (isSold(i.status) && !i.postedByAdmin) s.staleSold++;
    });
    s.completed = archive.length;
    s.totalFee = feeLedger.reduce((sum, e) => sum + (Number(e.feeKRW) || 0), 0);
    return s;
  }, [items, archive, feeLedger]);

  const statusCounts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach((st) => { c[st] = items.filter((i) => (st === 'sold' ? isSold(i.status) : i.status === st)).length; });
    return c;
  }, [items]);

  const filteredItems = useMemo(() => items.filter((i) => {
    if (statusFilter !== 'all' && (statusFilter === 'sold' ? !isSold(i.status) : i.status !== statusFilter)) return false;
    if (sourceFilter === 'admin' && !i.postedByAdmin) return false;
    if (sourceFilter === 'user' && i.postedByAdmin) return false;
    if (typeFilter !== 'all' && i.type !== typeFilter) return false;
    if (!search) return true;
    return [i.title, i.description, i.userName, i.userEmail, i.location, i.id]
      .some((v) => String(v || '').toLowerCase().includes(search));
  }), [items, statusFilter, sourceFilter, typeFilter, search]);

  const filteredArchive = useMemo(() => {
    if (!search) return archive;
    return archive.filter((a) => [a.title, a.userName, a.userEmail, a.id].some((v) => String(v || '').toLowerCase().includes(search)));
  }, [archive, search]);

  // ---------- 사진 업로드 ----------
  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    if (!user?.uid) {
      message.error(T('msg.loginRequired'));
      return;
    }
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      message.warning(T('form.maxPhotos', { n: MAX_PHOTOS }));
      return;
    }
    const accepted = files.slice(0, room).filter((f) => f.size <= MAX_PHOTO_BYTES);
    if (accepted.length < Math.min(files.length, room)) message.warning(T('form.tooLarge'));
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of accepted) {
        const safeName = file.name.replace(/[^\w.-]+/g, '_');
        const path = `marketplace/${user.uid}/${Date.now()}-${safeName}`;
        const snap = await uploadBytes(ref(storage, path), file, { contentType: file.type });
        uploaded.push({ url: await getDownloadURL(snap.ref), path, isNew: true });
      }
      setPhotos((prev) => [...prev, ...uploaded]);
    } catch (error) {
      logger.error('marketplace photo upload failed', { error: error.message });
      message.error(T('form.uploadFailed', { error: error.message }));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = async (index) => {
    const target = photos[index];
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    if (target?.isNew && target.path) {
      deleteObject(ref(storage, target.path)).catch(() => {});
    }
  };

  const addPhotoUrl = (url) => {
    const v = String(url || '').trim();
    if (!/^https?:\/\//.test(v)) return;
    if (photos.length >= MAX_PHOTOS) {
      message.warning(T('form.maxPhotos', { n: MAX_PHOTOS }));
      return;
    }
    setPhotos((prev) => [...prev, { url: v }]);
    form.setFieldValue('photoUrl', '');
  };

  // ---------- 등록 / 수정 ----------
  const openCreate = () => {
    setEditing(null);
    setPhotos([]);
    form.resetFields();
    form.setFieldsValue({ type: 'selling', status: 'available', category: 'other', condition: 'good', feeWaived: true });
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setPhotos((item.photos.length ? item.photos : item.imageUrl ? [item.imageUrl] : []).map((url) => ({ url })));
    form.resetFields();
    form.setFieldsValue({
      title: item.title,
      description: item.description,
      price: item.price === '' ? null : parsePrice(item.price),
      location: item.location,
      category: item.category,
      condition: item.condition,
      type: item.type,
      status: isSold(item.status) ? 'sold' : item.status,
      feeWaived: item.feeWaived !== false
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving || uploading) return;
    if (!editing) photos.filter((p) => p.isNew && p.path).forEach((p) => deleteObject(ref(storage, p.path)).catch(() => {}));
    setFormOpen(false);
    setEditing(null);
    setPhotos([]);
  };

  const handleSubmit = async (values) => {
    if (!user?.uid) {
      message.error(T('msg.loginRequired'));
      return;
    }
    setSaving(true);
    try {
      const photoUrls = photos.map((p) => p.url);
      const status = values.status || 'available';
      const common = {
        title: String(values.title || '').trim(),
        description: String(values.description || '').trim(),
        price: values.price != null && values.price !== '' ? String(values.price) : '',
        location: String(values.location || '').trim(),
        category: values.category || 'other',
        condition: values.condition || 'good',
        type: values.type || 'selling',
        status,
        photos: photoUrls,
        imageUrl: photoUrls[0] || '',
        isActive: isLive(status),
        updatedAt: serverTimestamp()
      };
      if (editing) {
        await updateDoc(doc(db, ITEMS_PATH, editing.id), {
          ...common,
          ...(editing.postedByAdmin ? { feeWaived: values.feeWaived !== false } : {})
        });
        message.success(T('msg.updated'));
      } else {
        await addDoc(collection(db, ITEMS_PATH), {
          ...common,
          likes: 0,
          userId: user.uid,
          userName: sellerName,
          userEmail: sellerEmail,
          postedByAdmin: true,
          isOfficial: true,
          sellerLabel: 'Kairos',
          feeWaived: values.feeWaived !== false,
          isApproved: true,
          createdAt: serverTimestamp()
        });
        message.success(T('msg.created'));
      }
      setFormOpen(false);
      setEditing(null);
      setPhotos([]);
      await loadItems();
    } catch (error) {
      logger.error('save marketplace item failed', { error: error.message });
      message.error(T(editing ? 'msg.updateFailed' : 'msg.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ---------- 상태 변경 / 삭제 ----------
  const patchItem = async (item, patch, successKey) => {
    try {
      await updateDoc(doc(db, ITEMS_PATH, item.id), { ...patch, updatedAt: serverTimestamp() });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ...patch, updatedAt: new Date() } : i)));
      if (viewItem?.id === item.id) setViewItem((v) => ({ ...v, ...patch }));
      message.success(T(successKey));
    } catch (error) {
      message.error(T('msg.actionFailed', { error: error.message }));
    }
  };

  const markOfficialSold = (item) => {
    const { amount } = calcFee(item.price);
    patchItem(item, {
      status: 'sold',
      soldAt: serverTimestamp(),
      completedAt: serverTimestamp(),
      completedBy: user?.uid || 'admin',
      priceKRW: amount,
      feeKRW: 0,
      netAmountKRW: amount,
      feeRate: FEE_RATE,
      feeWaived: true,
      feeCharged: false,
      isActive: false
    }, 'msg.markedSold');
  };

  const confirmDelete = (item) => {
    Modal.confirm({
      title: T('delete.title'),
      content: T(item.postedByAdmin ? 'delete.bodyOfficial' : 'delete.bodyUser', { title: item.title || T('common.untitled') }),
      okText: T('delete.ok'),
      okButtonProps: { danger: true },
      cancelText: T('common.cancel'),
      onOk: async () => {
        try {
          await deleteDoc(doc(db, ITEMS_PATH, item.id));
          if (item.postedByAdmin && item.userId === user?.uid) {
            item.photos.forEach((url) => deleteObject(ref(storage, url)).catch(() => {}));
          }
          setItems((prev) => prev.filter((i) => i.id !== item.id));
          if (viewItem?.id === item.id) setViewItem(null);
          message.success(T('msg.deleted'));
        } catch (error) {
          message.error(T('msg.actionFailed', { error: error.message }));
        }
      }
    });
  };

  const purgeCompleted = () => {
    Modal.confirm({
      title: T('purge.title'),
      content: T('purge.body', { n: stats.staleSold }),
      okText: T('purge.ok'),
      cancelText: T('common.cancel'),
      onOk: async () => {
        setPurging(true);
        try {
          const { data } = await apiClient.post('/api/marketplace/admin/purge-completed');
          message.success(T('purge.done', { n: data?.removed ?? 0 }));
          await loadAll();
        } catch (error) {
          message.error(T('purge.failed', { error: error.message }));
        } finally {
          setPurging(false);
        }
      }
    });
  };

  // ---------- 표시 헬퍼 ----------
  const StatusPill = ({ status }) => (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${STATUS_CLS[status] || STATUS_CLS.inactive}`}>
      {T(`status.${isSold(status) ? 'sold' : status}`, status)}
    </span>
  );

  const SourceBadge = ({ official }) => (
    official ? (
      <span className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 ring-1 ring-blue-200">
        <Sparkles className="h-2.5 w-2.5" />{T('source.admin')}
      </span>
    ) : null
  );

  const priceCell = (price) => (parsePrice(price) > 0
    ? <span className="font-semibold tabular-nums text-slate-900">{won(parsePrice(price))}</span>
    : <span className="text-slate-400">{T('common.negotiable')}</span>);

  const iconBtn = (title, Icon, onClick, tone = 'text-slate-600 hover:bg-slate-100') => (
    <Tooltip title={title}>
      <button type="button" onClick={onClick} className={`rounded-md p-1.5 ${tone}`}>
        <Icon className="h-4 w-4" />
      </button>
    </Tooltip>
  );

  const itemColumns = [
    {
      title: T('col.item'),
      key: 'item',
      render: (_, r) => (
        <button type="button" onClick={() => setViewItem(r)} className="flex min-w-0 items-center gap-2.5 text-left">
          <Thumb url={r.imageUrl} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[13px] font-semibold text-slate-900">{r.title || T('common.untitled')}</span>
              <SourceBadge official={r.postedByAdmin} />
            </div>
            <div className="truncate text-[11px] text-slate-500">
              {T(`type.${r.type}`, r.type)} · {T(`category.${r.category}`, r.category)}{r.location ? ` · ${r.location}` : ''}
            </div>
          </div>
        </button>
      )
    },
    { title: T('col.price'), key: 'price', width: 120, render: (_, r) => priceCell(r.price) },
    {
      title: T('col.fee'),
      key: 'fee',
      width: 110,
      render: (_, r) => {
        if (r.postedByAdmin && r.feeWaived !== false) return <span className="text-[11px] text-slate-400">{T('fee.waivedOfficial')}</span>;
        const { feeKRW } = calcFee(r.price);
        return feeKRW > 0 ? <span className="text-[12px] tabular-nums text-amber-700">{won(feeKRW)}</span> : <span className="text-slate-300">—</span>;
      }
    },
    {
      title: T('col.seller'),
      key: 'seller',
      width: 170,
      render: (_, r) => (
        <div className="min-w-0">
          <div className="truncate text-[12px] text-slate-800">{r.userName || '—'}</div>
          <div className="truncate text-[11px] text-slate-400">{r.userEmail || r.userId}</div>
        </div>
      )
    },
    { title: T('col.status'), key: 'status', width: 96, render: (_, r) => <StatusPill status={r.status} /> },
    { title: T('col.created'), key: 'createdAt', width: 100, render: (_, r) => <span className="text-[11px] tabular-nums text-slate-500">{fmtDate(r.createdAt)}</span> },
    {
      title: '',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, r) => (
        <div className="flex items-center justify-end gap-0.5">
          {iconBtn(T('action.view'), Eye, () => setViewItem(r))}
          {iconBtn(T('action.edit'), Edit, () => openEdit(r))}
          {r.postedByAdmin && isLive(r.status) && iconBtn(T('action.markSold'), CheckCircle, () => markOfficialSold(r), 'text-emerald-600 hover:bg-emerald-50')}
          {r.postedByAdmin && isSold(r.status) && iconBtn(T('action.relist'), RotateCcw, () => patchItem(r, { status: 'available', isActive: true }, 'msg.relisted'), 'text-emerald-600 hover:bg-emerald-50')}
          {!r.postedByAdmin && isLive(r.status) && iconBtn(T('action.hide'), EyeOff, () => patchItem(r, { status: 'inactive', isActive: false }, 'msg.hidden'), 'text-amber-600 hover:bg-amber-50')}
          {!r.postedByAdmin && r.status === 'inactive' && iconBtn(T('action.unhide'), RotateCcw, () => patchItem(r, { status: 'available', isActive: true }, 'msg.unhidden'), 'text-emerald-600 hover:bg-emerald-50')}
          {iconBtn(T('action.delete'), Trash2, () => confirmDelete(r), 'text-red-600 hover:bg-red-50')}
        </div>
      )
    }
  ];

  const archiveColumns = [
    {
      title: T('col.item'),
      key: 'item',
      render: (_, r) => (
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-slate-900">{r.title || T('common.untitled')}</div>
          <div className="truncate text-[11px] text-slate-500">{T(`category.${r.category}`, r.category)} · {r.id}</div>
        </div>
      )
    },
    { title: T('col.price'), key: 'price', width: 110, render: (_, r) => priceCell(r.priceKRW || r.price) },
    { title: T('col.fee'), key: 'fee', width: 100, render: (_, r) => (r.feeWaived || !r.feeKRW ? <span className="text-[11px] text-slate-400">{T('fee.none')}</span> : <span className="tabular-nums text-amber-700">{won(r.feeKRW)}</span>) },
    {
      title: T('col.seller'),
      key: 'seller',
      width: 170,
      render: (_, r) => (
        <div className="min-w-0">
          <div className="truncate text-[12px] text-slate-800">{r.userName || '—'}</div>
          <div className="truncate text-[11px] text-slate-400">{r.userEmail || r.userId}</div>
        </div>
      )
    },
    {
      title: T('col.reason'),
      key: 'reason',
      width: 120,
      render: (_, r) => (
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${r.archiveReason === 'trade_completed' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
          {T(`reason.${r.archiveReason || 'other'}`, r.archiveReason)}
        </span>
      )
    },
    { title: T('col.completedAt'), key: 'completedAt', width: 130, render: (_, r) => <span className="text-[11px] tabular-nums text-slate-500">{fmtDate(r.completedAt || r.archivedAt, true)}</span> }
  ];

  const feeColumns = [
    { title: T('col.date'), key: 'createdAt', width: 140, render: (_, r) => <span className="text-[11px] tabular-nums text-slate-500">{fmtDate(r.createdAt, true)}</span> },
    {
      title: T('col.item'),
      key: 'item',
      render: (_, r) => (
        <div className="min-w-0">
          <div className="truncate text-[13px] font-medium text-slate-900">{r.title || T('common.untitled')}</div>
          <div className="truncate text-[11px] text-slate-400">{r.itemId}</div>
        </div>
      )
    },
    { title: T('col.price'), key: 'priceKRW', width: 110, render: (_, r) => <span className="tabular-nums">{won(r.priceKRW)}</span> },
    { title: T('col.feeRate', { n: 3 }), key: 'feeKRW', width: 110, render: (_, r) => <span className="font-semibold tabular-nums text-amber-700">{won(r.feeKRW)}</span> },
    { title: T('col.net'), key: 'netAmountKRW', width: 110, render: (_, r) => <span className="tabular-nums">{won(r.netAmountKRW)}</span> },
    { title: T('col.sellerId'), key: 'sellerId', width: 150, render: (_, r) => <span className="font-mono text-[11px] text-slate-500">{r.sellerId || '—'}</span> }
  ];

  const TABS = [
    { key: 'items', label: T('tab.items'), Icon: Store, count: items.length },
    { key: 'archive', label: T('tab.archive'), Icon: Archive, count: archive.length },
    { key: 'fees', label: T('tab.fees'), Icon: BadgeDollarSign, count: feeLedger.length }
  ];

  const STAT_CHIPS = [
    { label: T('stat.available'), value: stats.available, tone: 'border-emerald-200 bg-emerald-50/50' },
    { label: T('stat.reserved'), value: stats.reserved, tone: 'border-amber-200 bg-amber-50/50' },
    { label: T('stat.official'), value: stats.official, tone: 'border-blue-200 bg-blue-50/50' },
    { label: T('stat.completed'), value: stats.completed, tone: 'border-slate-200 bg-slate-50' },
    { label: T('stat.totalFee'), value: won(stats.totalFee), tone: 'border-amber-200 bg-amber-50/50' }
  ];

  const pill = (active) => `inline-flex !h-7 !min-h-0 items-center gap-1 rounded-full !px-2.5 !py-0 text-[11px] font-semibold transition-colors ${
    active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100'
  }`;

  const fee = calcFee(priceValue);
  const showFeePreview = !(feeWaivedValue && (!editing || editing.postedByAdmin)) && fee.amount > 0;

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="space-y-3">
        {/* Header */}
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500">
                <Store className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-[15px] font-semibold leading-tight text-slate-900">{T('title')}</h1>
                <p className="text-[11px] leading-tight text-slate-500">{T('subtitle')}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {STAT_CHIPS.map((c) => (
                <div key={c.label} className={`rounded-lg border px-2.5 py-1 ${c.tone}`}>
                  <span className="block text-[10px] leading-tight text-slate-500">{c.label}</span>
                  <span className="block text-sm font-bold leading-tight tabular-nums text-slate-900">{typeof c.value === 'number' ? c.value.toLocaleString() : c.value}</span>
                </div>
              ))}
              <button type="button" onClick={loadAll} disabled={loading} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                {T('action.refresh')}
              </button>
              <button type="button" onClick={openCreate} className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}>
                <Plus className="h-3.5 w-3.5" />
                {T('action.create')}
              </button>
            </div>
          </div>

          <div className="-mx-1 mt-3 flex gap-1 overflow-x-auto border-t border-slate-100 px-1 pt-2.5">
            {TABS.map(({ key, label, Icon, count }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`inline-flex !h-8 !min-h-0 flex-shrink-0 items-center gap-1.5 rounded-lg !px-3 !py-0 text-xs font-semibold transition-colors ${active ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>{count.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        </div>

        {stats.staleSold > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            <span>{T('purge.banner', { n: stats.staleSold })}</span>
            <button type="button" onClick={purgeCompleted} disabled={purging} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`}>
              <Archive className="h-3.5 w-3.5" />
              {purging ? T('purge.running') : T('purge.ok')}
            </button>
          </div>
        )}

        {historyError && activeTab !== 'items' && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{T('msg.historyFailed', { error: historyError })}</div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {/* Toolbar */}
          <div className="space-y-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[220px] flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={T('toolbar.search')}
                  className="h-8 w-full rounded-md border border-slate-200 bg-white pl-8 pr-7 text-xs text-slate-800 outline-none focus:border-slate-400"
                />
                {searchInput && (
                  <button type="button" onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {activeTab === 'items' && (
                <>
                  <Select
                    size="small"
                    value={typeFilter}
                    onChange={setTypeFilter}
                    className="w-32"
                    options={[{ value: 'all', label: T('toolbar.allTypes') }, { value: 'selling', label: T('type.selling') }, { value: 'buying', label: T('type.buying') }]}
                  />
                  <Select
                    size="small"
                    value={sourceFilter}
                    onChange={setSourceFilter}
                    className="w-36"
                    options={[{ value: 'all', label: T('toolbar.allSources') }, { value: 'admin', label: T('source.admin') }, { value: 'user', label: T('source.user') }]}
                  />
                </>
              )}
            </div>
            {activeTab === 'items' && (
              <div className="flex flex-wrap gap-1">
                {['all', ...STATUSES].map((st) => (
                  <button key={st} type="button" onClick={() => setStatusFilter(st)} className={pill(statusFilter === st)}>
                    {st === 'all' ? T('toolbar.allStatuses') : T(`status.${st}`)}
                    <span className={`tabular-nums ${statusFilter === st ? 'text-white/70' : 'text-slate-400'}`}>{statusCounts[st] ?? 0}</span>
                  </button>
                ))}
              </div>
            )}
            {activeTab === 'archive' && <div className="text-[11px] text-slate-500">{T('archive.hint')}</div>}
            {activeTab === 'fees' && <div className="text-[11px] text-slate-500">{T('fees.hint')}</div>}
          </div>

          {activeTab === 'items' && (
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              columns={itemColumns}
              dataSource={filteredItems}
              scroll={{ x: 1000 }}
              pagination={{ defaultPageSize: 20, showSizeChanger: true, size: 'small', showTotal: (n) => T('common.total', { n }) }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={T('empty.items')} /> }}
            />
          )}
          {activeTab === 'archive' && (
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              columns={archiveColumns}
              dataSource={filteredArchive}
              scroll={{ x: 900 }}
              pagination={{ defaultPageSize: 20, showSizeChanger: true, size: 'small', showTotal: (n) => T('common.total', { n }) }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={T('empty.archive')} /> }}
            />
          )}
          {activeTab === 'fees' && (
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              columns={feeColumns}
              dataSource={feeLedger}
              scroll={{ x: 800 }}
              pagination={{ defaultPageSize: 20, showSizeChanger: true, size: 'small' }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={T('empty.fees')} /> }}
              summary={() => (feeLedger.length ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}><span className="text-xs font-semibold">{T('fees.total')}</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}><span className="text-xs font-bold tabular-nums text-amber-700">{won(stats.totalFee)}</span></Table.Summary.Cell>
                  <Table.Summary.Cell index={4} colSpan={2} />
                </Table.Summary.Row>
              ) : null)}
            />
          )}
        </div>
      </div>

      {/* Create / Edit */}
      <Modal
        open={formOpen}
        onCancel={closeForm}
        width={680}
        destroyOnClose
        title={<span className="text-sm font-semibold">{editing ? T('form.editTitle') : T('form.createTitle')}</span>}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeForm} disabled={saving || uploading} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>{T('common.cancel')}</button>
            <button type="button" onClick={() => form.submit()} disabled={saving || uploading} className={`${btn} bg-slate-900 text-white hover:bg-slate-800`}>
              {saving && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
              {editing ? T('form.save') : T('form.submit')}
            </button>
          </div>
        }
      >
        {!editing && (
          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
            {T('form.officialNote', { name: sellerName, email: sellerEmail })}
          </div>
        )}
        {editing && !editing.postedByAdmin && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            {T('form.userItemNote', { name: editing.userName || editing.userEmail })}
          </div>
        )}

        <div className="mb-4">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">{T('form.photos')} <span className="font-normal text-slate-400">({photos.length}/{MAX_PHOTOS})</span></span>
            <span className="text-[10px] text-slate-400">{T('form.photosHint')}</span>
          </div>
          <div
            className="flex flex-wrap gap-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          >
            {photos.map((p, i) => (
              <div key={`${p.url}-${i}`} className="group relative">
                <img src={p.url} alt="" className="h-20 w-20 rounded-lg border border-slate-200 object-cover" />
                {i === 0 && <span className="absolute bottom-1 left-1 rounded bg-slate-900/70 px-1 text-[9px] font-semibold text-white">{T('form.cover')}</span>}
                <button type="button" onClick={() => removePhoto(i)} className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-slate-500 shadow ring-1 ring-slate-200 hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-50 disabled:opacity-60"
              >
                {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                <span className="text-[10px]">{uploading ? T('form.uploading') : T('form.addPhoto')}</span>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false} className="[&_.ant-form-item]:!mb-3">
          <Form.Item name="title" label={T('form.title')} rules={[{ required: true, message: T('form.titleRequired') }]}>
            <Input placeholder={T('form.titlePlaceholder')} maxLength={80} showCount />
          </Form.Item>
          <Form.Item name="description" label={T('form.description')} rules={[{ required: true, message: T('form.descriptionRequired') }]}>
            <Input.TextArea rows={4} placeholder={T('form.descriptionPlaceholder')} maxLength={2000} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-3 sm:grid-cols-3">
            <Form.Item name="type" label={T('form.type')}>
              <Select options={['selling', 'buying'].map((v) => ({ value: v, label: T(`type.${v}`) }))} />
            </Form.Item>
            <Form.Item name="price" label={T('form.price')}>
              <InputNumber
                min={0}
                step={1000}
                className="!w-full"
                placeholder={T('form.pricePlaceholder')}
                formatter={(v) => (v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                parser={(v) => (v ? v.replace(/[^\d]/g, '') : '')}
                addonBefore="₩"
              />
            </Form.Item>
            <Form.Item name="status" label={T('form.status')}>
              <Select options={STATUSES.map((v) => ({ value: v, label: T(`status.${v}`) }))} />
            </Form.Item>
            <Form.Item name="category" label={T('form.category')}>
              <Select options={CATEGORIES.map((v) => ({ value: v, label: T(`category.${v}`) }))} />
            </Form.Item>
            <Form.Item name="condition" label={T('form.condition')}>
              <Select options={CONDITIONS.map((v) => ({ value: v, label: T(`condition.${v}`) }))} />
            </Form.Item>
            <Form.Item name="location" label={T('form.location')}>
              <Input placeholder={T('form.locationPlaceholder')} />
            </Form.Item>
          </div>
          <Form.Item name="photoUrl" label={T('form.photoUrl')} className="!mb-2">
            <Input.Search placeholder="https://..." enterButton={T('form.addUrl')} onSearch={addPhotoUrl} />
          </Form.Item>
          {(!editing || editing.postedByAdmin) && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <div className="text-xs font-semibold text-slate-700">{T('form.feeWaived')}</div>
                <div className="text-[10px] text-slate-500">{T('form.feeWaivedHint')}</div>
              </div>
              <Form.Item name="feeWaived" valuePropName="checked" noStyle>
                <Switch size="small" />
              </Form.Item>
            </div>
          )}
          {showFeePreview && (
            <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              {T('form.feePreview', { fee: won(fee.feeKRW), net: won(fee.netAmountKRW) })}
            </div>
          )}
        </Form>
      </Modal>

      {/* Detail */}
      <Drawer
        open={!!viewItem}
        onClose={() => setViewItem(null)}
        width={460}
        title={viewItem ? <span className="text-sm font-semibold">{viewItem.title || T('common.untitled')}</span> : null}
        extra={viewItem && <StatusPill status={viewItem.status} />}
      >
        {viewItem && (
          <div className="space-y-4 text-xs">
            {viewItem.photos.length > 0 ? (
              <div className="grid grid-cols-3 gap-1.5">
                {viewItem.photos.map((url, i) => (
                  <a key={`${url}-${i}`} href={url} target="_blank" rel="noreferrer" className={i === 0 ? 'col-span-3' : ''}>
                    <img src={url} alt="" className={`w-full rounded-lg border border-slate-200 object-cover ${i === 0 ? 'h-56' : 'h-24'}`} />
                  </a>
                ))}
              </div>
            ) : (
              <div className="flex h-32 items-center justify-center rounded-lg bg-slate-100 text-slate-400"><ShoppingBag className="h-6 w-6" /></div>
            )}
            <div className="flex items-center justify-between">
              <div className="text-lg font-bold text-slate-900">{priceCell(viewItem.price)}</div>
              <SourceBadge official={viewItem.postedByAdmin} />
            </div>
            <div className="grid grid-cols-[84px_1fr] gap-y-1.5 rounded-lg border border-slate-200 p-3">
              <span className="text-slate-500">{T('form.type')}</span><span>{T(`type.${viewItem.type}`, viewItem.type)}</span>
              <span className="text-slate-500">{T('form.category')}</span><span>{T(`category.${viewItem.category}`, viewItem.category)}</span>
              <span className="text-slate-500">{T('form.condition')}</span><span>{T(`condition.${viewItem.condition}`, viewItem.condition)}</span>
              <span className="text-slate-500">{T('form.location')}</span><span>{viewItem.location || '—'}</span>
              <span className="text-slate-500">{T('col.seller')}</span><span className="break-all">{viewItem.userName || '—'} · {viewItem.userEmail || viewItem.userId}</span>
              <span className="text-slate-500">{T('detail.likes')}</span><span>{viewItem.likes}</span>
              <span className="text-slate-500">{T('col.created')}</span><span>{fmtDate(viewItem.createdAt, true)}</span>
              <span className="text-slate-500">ID</span><span className="break-all font-mono text-[11px] text-slate-500">{viewItem.id}</span>
            </div>
            <div className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 leading-relaxed text-slate-700">{viewItem.description || '—'}</div>
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              <button type="button" onClick={() => openEdit(viewItem)} className={`${btn} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
                <Edit className="h-3.5 w-3.5" />{T('action.edit')}
              </button>
              {viewItem.postedByAdmin && isLive(viewItem.status) && (
                <button type="button" onClick={() => markOfficialSold(viewItem)} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}>
                  <CheckCircle className="h-3.5 w-3.5" />{T('action.markSold')}
                </button>
              )}
              <button type="button" onClick={() => confirmDelete(viewItem)} className={`${btn} border border-red-200 bg-white text-red-600 hover:bg-red-50`}>
                <Trash2 className="h-3.5 w-3.5" />{T('action.delete')}
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default MarketplaceManagementPage;
